import React, { useMemo } from 'react';
import { AlertTriangle } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { Erod } from '@cotera/era';
import { PersistedWorkflow } from '@cotera/sdk/core';
import { Assert } from '@cotera/utilities';
import { Tooltip } from '@cotera/client/app/components';
import { useToolsAsync } from '@cotera/client/app/hooks/use-tools-info';
import { useTenantedClient, useWhoami } from '@cotera/client/app/stores/org';
import { queryKeys } from '@cotera/client/app/hooks/query-cache-keys';
import { useOpenToolModal } from '@cotera/client/v0/components/tool-connections';
import type { WorkflowGraphNodeFlowRenderProps } from '../../workflow-render-catalog/types';
import { asRecord, readString } from '../utils';
import { listStructFields } from './edit-strategies/tool-input-schema';
import { isToolInputConfigured } from './edit-strategies/tool-inputs-editor';
import { toolInputFieldLabel } from './edit-strategies/tool-input-renderers';
import { readParamBindings } from './edit-strategies/json-pointer-suggestions';

const { WorkflowNodeType } = PersistedWorkflow;

function useToolExecutableMap(): Map<string, boolean> {
  const client = useTenantedClient();
  const orgId = useWhoami((s) => s.org.id);
  const { data } = useQuery({
    queryKey: queryKeys.toolsV2.all({ orgId }),
    queryFn: async () => Assert.assertOk(await client.toolsV2.list({})),
  });
  return useMemo(() => {
    const map = new Map<string, boolean>();
    for (const tool of data ?? []) {
      map.set(tool.key, tool.canExecute);
    }
    return map;
  }, [data]);
}

function useMissingRequiredInputMap(): Map<string, readonly string[]> {
  const { tools } = useToolsAsync();
  return useMemo(() => {
    const map = new Map<string, readonly string[]>();
    for (const tool of tools) {
      const fields = listStructFields(
        Erod.Schema.bareSchemaFromTy(tool.inputTy),
        tool.inputFieldDescriptions
      );
      map.set(
        tool.key,
        fields.filter((field) => field.required).map((field) => field.name)
      );
    }
    return map;
  }, [tools]);
}

/** Amber badge when a tool-call node references an unconnected integration. */
export const ToolCallFlowAffordances: React.FC<
  WorkflowGraphNodeFlowRenderProps
> = ({ node }) => {
  const openToolModal = useOpenToolModal();
  const executableMap = useToolExecutableMap();
  const missingRequiredInputMap = useMissingRequiredInputMap();

  if (node.type !== WorkflowNodeType.ToolCall) {
    return null;
  }

  const data = asRecord(node.data);
  const toolKey = readString(data, 'key');
  const params = asRecord(data?.['params']) ?? {};
  const bindings = readParamBindings(data);
  const missingRequiredInputKeys =
    toolKey === undefined ? [] : missingRequiredInputMap.get(toolKey);
  const needsSetup =
    toolKey !== undefined &&
    executableMap.size > 0 &&
    executableMap.get(toolKey) === false;
  const missingRequiredInputsForNode =
    missingRequiredInputKeys === undefined
      ? []
      : missingRequiredInputKeys.filter(
          (name) => !isToolInputConfigured(name, params, bindings)
        );
  const needsInputConfiguration =
    toolKey === undefined ||
    (missingRequiredInputKeys !== undefined &&
      missingRequiredInputsForNode.length > 0);

  if (!needsSetup && !needsInputConfiguration) {
    return null;
  }

  if (needsInputConfiguration) {
    const tooltipContent =
      toolKey === undefined
        ? 'Tool not configured'
        : `Required input${
            missingRequiredInputsForNode.length === 1 ? '' : 's'
          } not mapped: ${missingRequiredInputsForNode
            // Named as the drawer names them: pointing at a `datasetId` the
            // editor calls "dataset" is a worse instruction than no name.
            .map((name) => toolInputFieldLabel(toolKey, name))
            .join(', ')}`;
    return (
      <Tooltip side="top" tooltipContent={tooltipContent}>
        <span
          title={tooltipContent}
          aria-label={tooltipContent}
          className="flex h-5 w-5 items-center justify-center rounded-full border bg-background text-amber-500 shadow-sm"
        >
          <AlertTriangle className="h-3 w-3" />
        </span>
      </Tooltip>
    );
  }

  return (
    <button
      type="button"
      title="Not connected — set up this tool to run"
      aria-label="Tool not connected — set up to run"
      className="nodrag flex h-5 w-5 items-center justify-center rounded-full border bg-background text-amber-500 shadow-sm"
      onClick={(e) => {
        e.stopPropagation();
        openToolModal(toolKey);
      }}
    >
      <AlertTriangle className="h-3 w-3" />
    </button>
  );
};
